import React, { useState, useEffect, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import {
  Search, CheckCircle2, Edit2, Merge, X, ChevronLeft, ChevronRight,
  Disc3, RefreshCw, AlertTriangle, User, ExternalLink, Trash2, Crown,
  ArrowRight, Zap, ShieldAlert, Loader2, Plus, Eye, EyeOff, Power,
} from "lucide-react";
import api from "../../utils/api";
import { toast } from "sonner";

const inp = "w-full px-3 py-2 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-rose-500/40 bg-zinc-900 border-zinc-700 text-white placeholder-zinc-500";

// ── Edit Modal ────────────────────────────────────────────────────────────────
function EditModal({ label, token, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:           label.name || "",
    bio:            label.bio || "",
    logo_url:       label.logo_url || "",
    founded_year:   label.founded_year || "",
    country:        label.country || "",
    is_verified:    label.is_verified || false,
    is_active:      label.is_active !== false, // bestehende Labels default aktiv
    owner_user_id:  label.owner_user_id || "",
    social_links: {
      website:    label.social_links?.website    || "",
      beatport:   label.social_links?.beatport   || "",
      spotify:    label.social_links?.spotify    || "",
      soundcloud: label.social_links?.soundcloud || "",
      youtube:    label.social_links?.youtube    || "",
    },
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/labels/admin/${label.id}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Label gespeichert");
      onSaved();
    } catch (e) {
      toast.error("Fehler: " + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="w-full max-w-lg rounded-2xl shadow-2xl bg-zinc-900 text-white flex flex-col max-h-[90vh] border border-white/10">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h2 className="font-black text-lg">Label bearbeiten</h2>
          <button onClick={onClose}><X size={18} className="text-zinc-400 hover:text-white" /></button>
        </div>
        <div className="overflow-y-auto p-5 space-y-4 flex-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs text-zinc-400 mb-1 block">Name</label>
              <input className={inp} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Logo URL</label>
              <input className={inp} value={form.logo_url} onChange={e => setForm(f => ({ ...f, logo_url: e.target.value }))} placeholder="https://..." />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Land</label>
              <input className={inp} value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} placeholder="DE" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Gegründet</label>
              <input className={inp} type="number" value={form.founded_year} onChange={e => setForm(f => ({ ...f, founded_year: e.target.value ? +e.target.value : null }))} placeholder="2008" />
            </div>
            <div className="flex items-center gap-2 pt-4">
              <input type="checkbox" id="verified" checked={form.is_verified} onChange={e => setForm(f => ({ ...f, is_verified: e.target.checked }))} className="accent-emerald-500" />
              <label htmlFor="verified" className="text-sm text-zinc-300">Intern verifiziert (Admin-Flag)</label>
            </div>
            <div className="col-span-2">
              <label className="text-xs text-zinc-400 mb-2 block">Sichtbarkeit</label>
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
                data-testid="edit-is-active-toggle"
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${form.is_active ? "bg-emerald-500/10 border border-emerald-500/25" : "bg-zinc-800 border border-zinc-700"}`}
              >
                <div className="flex items-center gap-3">
                  {form.is_active
                    ? <Eye size={15} className="text-emerald-400" />
                    : <EyeOff size={15} className="text-zinc-500" />}
                  <div className="text-left">
                    <p className={`text-sm font-semibold ${form.is_active ? "text-emerald-300" : "text-zinc-400"}`}>
                      {form.is_active ? "Öffentlich sichtbar" : "Deaktiviert (versteckt)"}
                    </p>
                    <p className="text-xs text-zinc-600 mt-0.5">
                      {form.is_active ? "Label erscheint in der öffentlichen Liste" : "Label taucht nicht öffentlich auf"}
                    </p>
                  </div>
                </div>
                <div className={`relative w-11 h-6 rounded-full transition-all flex-shrink-0 ${form.is_active ? "bg-emerald-500" : "bg-zinc-700"}`}>
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${form.is_active ? "right-1" : "left-1"}`} />
                </div>
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Bio</label>
            <textarea className={`${inp} resize-none`} rows={3} value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Label Manager (Auth0 User ID)</label>
            <input className={inp} value={form.owner_user_id} onChange={e => setForm(f => ({ ...f, owner_user_id: e.target.value }))} placeholder="auth0|..." />
          </div>
          <div className="space-y-2">
            <label className="text-xs text-zinc-400 block">Social Links</label>
            {["website","beatport","spotify","soundcloud","youtube"].map(k => (
              <input key={k} className={inp} placeholder={k.charAt(0).toUpperCase() + k.slice(1)}
                value={form.social_links[k]}
                onChange={e => setForm(f => ({ ...f, social_links: { ...f.social_links, [k]: e.target.value } }))} />
            ))}
          </div>
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl bg-zinc-800 text-zinc-300 hover:bg-zinc-700">Abbrechen</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 text-sm font-semibold rounded-xl bg-rose-600 hover:bg-rose-500 text-white disabled:opacity-50">
            {saving ? "Speichert..." : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Merge Modal ───────────────────────────────────────────────────────────────
function MergeModal({ sourceLabel, token, onClose, onMerged }) {
  const [targetSearch, setTargetSearch] = useState("");
  const [results, setResults]   = useState([]);
  const [target, setTarget]     = useState(null);
  const [merging, setMerging]   = useState(false);

  const search = async () => {
    if (!targetSearch.trim()) return;
    try {
      const res = await api.get(`/labels/admin/all?search=${encodeURIComponent(targetSearch)}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResults((res.data.labels || []).filter(l => l.id !== sourceLabel.id));
    } catch {}
  };

  const merge = async () => {
    if (!target) return;
    setMerging(true);
    try {
      await api.post("/labels/admin/merge", { source_id: sourceLabel.id, target_id: target.id }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`"${sourceLabel.name}" → "${target.name}" zusammengeführt`);
      onMerged(sourceLabel.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    setMerging(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="w-full max-w-md rounded-2xl shadow-2xl bg-zinc-900 text-white p-6 space-y-4 border border-white/10">
        <div className="flex items-center justify-between">
          <h2 className="font-black text-lg">Label zusammenführen</h2>
          <button onClick={onClose}><X size={18} className="text-zinc-400 hover:text-white" /></button>
        </div>
        <p className="text-sm text-zinc-400">
          <span className="font-bold text-white">{sourceLabel.name}</span> wird ins Ziel-Label zusammengeführt.
          Alle Releases werden umgeschrieben, das Quell-Label wird gelöscht.
        </p>
        <div className="flex gap-2">
          <input value={targetSearch} onChange={e => setTargetSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && search()}
            placeholder="Ziel-Label suchen..."
            className="flex-1 px-3 py-2 rounded-lg text-sm border bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500 focus:outline-none" />
          <button onClick={search} className="px-3 py-2 bg-zinc-700 rounded-lg text-sm text-white hover:bg-zinc-600">
            <Search size={14} />
          </button>
        </div>
        {results.length > 0 && (
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {results.map(r => (
              <button key={r.id} onClick={() => setTarget(r)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${target?.id === r.id ? "bg-rose-600 text-white" : "hover:bg-zinc-800 text-zinc-300"}`}>
                {r.name} <span className="text-xs opacity-60">({r.release_count} Releases)</span>
              </button>
            ))}
          </div>
        )}
        {target && (
          <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm">
            <AlertTriangle size={14} className="inline mr-1" />
            {sourceLabel.release_count} Releases von „{sourceLabel.name}" → „{target.name}"
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl bg-zinc-800 text-zinc-300 hover:bg-zinc-700">Abbrechen</button>
          <button onClick={merge} disabled={!target || merging} className="px-4 py-2 text-sm font-semibold rounded-xl bg-rose-600 hover:bg-rose-500 text-white disabled:opacity-50">
            {merging ? "Mergen..." : "Zusammenführen"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Create Label Modal ────────────────────────────────────────────────────────
function CreateLabelModal({ token, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", bio: "", logo_url: "", founded_year: "", country: "",
    owner_user_id: "", is_active: false,
  });
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Name ist erforderlich"); return; }
    setSaving(true);
    try {
      const res = await api.post("/labels/admin/create", {
        ...form,
        founded_year: form.founded_year ? +form.founded_year : null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Label "${form.name}" erstellt`);
      onCreated(res.data);
    } catch (e) {
      toast.error("Fehler: " + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="w-full max-w-lg rounded-2xl shadow-2xl bg-zinc-900 text-white flex flex-col max-h-[90vh] border border-white/10">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Plus size={16} className="text-emerald-400" />
            <h2 className="font-black text-lg">Neues Label erstellen</h2>
          </div>
          <button onClick={onClose}><X size={18} className="text-zinc-400 hover:text-white" /></button>
        </div>
        <div className="overflow-y-auto p-5 space-y-4 flex-1">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Name *</label>
            <input className={inp} autoFocus value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              onKeyDown={e => e.key === "Enter" && create()}
              placeholder="z.B. Sunshine Records" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Logo URL</label>
              <input className={inp} value={form.logo_url}
                onChange={e => setForm(f => ({ ...f, logo_url: e.target.value }))} placeholder="https://..." />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Land</label>
              <input className={inp} value={form.country}
                onChange={e => setForm(f => ({ ...f, country: e.target.value }))} placeholder="DE" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Gegründet</label>
              <input className={inp} type="number" value={form.founded_year}
                onChange={e => setForm(f => ({ ...f, founded_year: e.target.value }))} placeholder="2008" />
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Bio</label>
            <textarea className={`${inp} resize-none`} rows={3} value={form.bio}
              onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} placeholder="Kurze Label-Beschreibung..." />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Label Manager (Auth0 User ID)</label>
            <input className={inp} value={form.owner_user_id}
              onChange={e => setForm(f => ({ ...f, owner_user_id: e.target.value }))} placeholder="auth0|..." />
            <p className="text-xs text-zinc-600 mt-1">Optional – kann später zugewiesen werden</p>
          </div>

          {/* Sichtbarkeit Toggle */}
          <button
            type="button"
            onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
            data-testid="create-is-active-toggle"
            className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl transition-all ${
              form.is_active
                ? "bg-emerald-500/10 border border-emerald-500/25"
                : "bg-zinc-800/60 border border-zinc-700"
            }`}
          >
            <div className="flex items-center gap-3">
              {form.is_active
                ? <Eye size={15} className="text-emerald-400" />
                : <EyeOff size={15} className="text-zinc-500" />}
              <div className="text-left">
                <p className={`text-sm font-semibold ${form.is_active ? "text-emerald-300" : "text-zinc-300"}`}>
                  {form.is_active ? "Sofort öffentlich sichtbar" : "Deaktiviert – noch nicht öffentlich"}
                </p>
                <p className="text-xs text-zinc-600 mt-0.5">
                  {form.is_active
                    ? "Label erscheint direkt in der öffentlichen Übersicht"
                    : "Ideal für Labels in Vorbereitung – später aktivieren"}
                </p>
              </div>
            </div>
            <div className={`relative w-11 h-6 rounded-full transition-all flex-shrink-0 ${form.is_active ? "bg-emerald-500" : "bg-zinc-700"}`}>
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${form.is_active ? "right-1" : "left-1"}`} />
            </div>
          </button>
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors">
            Abbrechen
          </button>
          <button onClick={create} disabled={saving || !form.name.trim()}
            data-testid="create-label-submit"
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50 transition-colors">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {saving ? "Erstellt..." : "Label erstellen"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Duplicate Group (inline merge/delete ohne Modal) ──────────────────────────
function DuplicateGroup({ group, token, onGroupChanged }) {
  const defaultKeep = [...group].sort((a, b) => b.release_count - a.release_count)[0];
  const [keepId, setKeepId]   = useState(defaultKeep?.id);
  const [loading, setLoading] = useState(null);

  const keepLabel = group.find(l => l.id === keepId) || defaultKeep;

  const mergeInto = async (source) => {
    if (!window.confirm(`"${source.name}" → "${keepLabel.name}" zusammenführen?\nAlle Releases werden umgeschrieben, das Quell-Label wird gelöscht.`)) return;
    setLoading(source.id);
    try {
      await api.post("/labels/admin/merge", { source_id: source.id, target_id: keepLabel.id }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`"${source.name}" → "${keepLabel.name}" zusammengeführt`);
      onGroupChanged(source.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Mergen"); }
    setLoading(null);
  };

  const deleteLabel = async (label) => {
    if (!window.confirm(`"${label.name}" löschen? (0 Releases)`)) return;
    setLoading(label.id);
    try {
      await api.delete(`/labels/admin/${label.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`"${label.name}" gelöscht`);
      onGroupChanged(label.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Löschen"); }
    setLoading(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {group.map(label => {
          const isKeep = label.id === keepId;
          const isBusy = loading === label.id;
          return (
            <div key={label.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
              isKeep ? "bg-emerald-700 border-emerald-500" : "bg-zinc-700 border-zinc-600"
            }`}>
              {isKeep
                ? <Crown size={12} className="text-white flex-shrink-0" />
                : <Disc3 size={12} className="text-zinc-300 flex-shrink-0" />
              }
              <button
                onClick={() => !isKeep && setKeepId(label.id)}
                className={`text-sm font-semibold leading-none whitespace-nowrap ${isKeep ? "text-white cursor-default" : "text-zinc-100 hover:text-white cursor-pointer"}`}
                title={isKeep ? "Wird behalten" : "Als Behalten wählen"}
              >
                {label.name}
              </button>
              <span className={`text-xs font-bold flex-shrink-0 ${label.release_count === 0 ? "text-red-300" : "text-white/80"}`}>
                ({label.release_count})
              </span>

              {!isKeep && (
                label.release_count === 0 ? (
                  <button onClick={() => deleteLabel(label)} disabled={isBusy} title="Löschen"
                    className="p-1 rounded bg-red-700 hover:bg-red-600 text-white disabled:opacity-40 transition-colors">
                    {isBusy ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Trash2 size={11} />}
                  </button>
                ) : (
                  <button onClick={() => mergeInto(label)} disabled={isBusy}
                    title={`→ "${keepLabel.name}" zusammenführen`}
                    className="flex items-center gap-1 px-2 py-0.5 rounded bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-40 transition-colors text-xs font-bold whitespace-nowrap">
                    {isBusy
                      ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <><ArrowRight size={10} /><span className="max-w-[120px] truncate">{keepLabel.name}</span></>
                    }
                  </button>
                )
              )}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-zinc-500">Klick auf Label-Namen = als „Behalten" wählen</p>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function LabelsAdminPage() {
  const { token } = useAuth();

  const [labels, setLabels]         = useState([]);
  const [total, setTotal]           = useState(0);
  const [search, setSearch]         = useState("");
  const [query, setQuery]           = useState("");
  const [page, setPage]             = useState(1);
  const [loading, setLoading]       = useState(false);
  const [editLabel, setEditLabel]   = useState(null);
  const [mergeLabel, setMergeLabel] = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [tab, setTab]               = useState("all");
  const [duplicates, setDuplicates] = useState([]);
  const [loadingDupes, setLoadingDupes] = useState(false);

  // Massen-Bereinigung State
  const [dedupeReport,   setDedupeReport]   = useState(null);   // dry-run Ergebnis
  const [dedupeRunning,  setDedupeRunning]  = useState(false);
  const [dedupeExecuted, setDedupeExecuted] = useState(null);   // execute Ergebnis
  const LIMIT = 50;

  const fetchLabels = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (query.trim()) params.set("search", query.trim());
      const res = await api.get(`/labels/admin/all?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLabels(res.data.labels || []);
      setTotal(res.data.total || 0);
    } catch {}
    setLoading(false);
  }, [token, page, query]);

  useEffect(() => { if (tab === "all") fetchLabels(); }, [tab, fetchLabels]);

  const fetchDuplicates = useCallback(async () => {
    if (!token) return;
    setLoadingDupes(true);
    try {
      const res = await api.get("/labels/admin/duplicates", { headers: { Authorization: `Bearer ${token}` } });
      setDuplicates(res.data || []);
    } catch {}
    setLoadingDupes(false);
  }, [token]);

  const deleteLabel = useCallback(async (labelId, labelName) => {
    if (!window.confirm(`"${labelName}" löschen? (0 Releases, kann nicht rückgängig gemacht werden)`)) return;
    try {
      await api.delete(`/labels/admin/${labelId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`"${labelName}" gelöscht`);
      // Lokal entfernen – kein Neu-Laden nötig
      setDuplicates(prev =>
        prev
          .map(group => group.filter(l => l.id !== labelId))
          .filter(group => group.length > 1)
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Löschen");
    }
  }, [token]);

  const runDedupe = async (dryRun) => {
    setDedupeRunning(true);
    try {
      const res = await api.post(
        "/labels/admin/deduplicate",
        { dry_run: dryRun },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (dryRun) {
        setDedupeReport(res.data);
        setDedupeExecuted(null);
      } else {
        setDedupeExecuted(res.data);
        setDedupeReport(null);
        fetchLabels();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler bei der Bereinigung");
    }
    setDedupeRunning(false);
  };

  useEffect(() => { if (tab === "duplicates") fetchDuplicates(); }, [tab, fetchDuplicates]);

  const pages = Math.ceil(total / LIMIT);

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black text-white font-heading">Labels</h1>
            <p className="text-sm text-zinc-400 mt-1">{total.toLocaleString("de-DE")} Labels in der Datenbank</p>
          </div>
          <div className="flex items-center gap-3">
            <a href="/labels" target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-white transition-colors">
              <ExternalLink size={13} /> Öffentlich
            </a>
            <button
              onClick={() => setCreateModal(true)}
              data-testid="create-label-btn"
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"
            >
              <Plus size={14} /> Neues Label
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-white/[0.08]">
          {[["all","Alle Labels"],["duplicates","Duplikat-Check"]].map(([k,l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors -mb-px ${tab === k ? "border-rose-500 text-rose-400" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
              {l}
            </button>
          ))}
        </div>

        {/* ── All Labels Tab ── */}
        {tab === "all" && (
          <>
            <div className="flex gap-3 mt-6">
              <div className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                  <input className={`${inp} pl-9 flex-1`} placeholder="Name suchen..." value={search}
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { setQuery(search); setPage(1); } }} />
                </div>
                <button onClick={() => { setQuery(search); setPage(1); }}
                  className="px-4 py-2 text-sm font-semibold bg-rose-600 hover:bg-rose-500 text-white rounded-lg transition-colors">
                  Suchen
                </button>
              </div>
              <button onClick={() => { setQuery(""); setSearch(""); setPage(1); }}
                className="px-3 py-2 rounded-lg text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors">
                <RefreshCw size={14} />
              </button>
            </div>

            <div className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs font-semibold uppercase tracking-wide text-zinc-500 bg-zinc-900/80">
                    <th className="text-left px-4 py-3">Label</th>
                    <th className="text-right px-4 py-3">Releases</th>
                    <th className="text-center px-4 py-3">Status</th>
                    <th className="text-center px-4 py-3">Gemanaged</th>
                    <th className="text-left px-4 py-3">Manager</th>
                    <th className="text-right px-4 py-3">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04]">
                  {loading ? (
                    <tr><td colSpan={5} className="text-center py-12 text-zinc-500">Lädt...</td></tr>
                  ) : labels.map(label => (
                    <tr key={label.id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0">
                            {label.logo_url ? <img src={label.logo_url} alt="" className="w-full h-full object-cover" /> : <Disc3 size={16} className="m-1.5 text-zinc-600" />}
                          </div>
                          <div>
                            <p className="font-semibold text-white truncate max-w-[200px]">{label.name}</p>
                            <p className="text-xs text-zinc-600">{label.slug}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-zinc-400">{label.release_count}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={async () => {
                            const newActive = label.is_active === false ? true : false;
                            try {
                              await api.put(`/labels/admin/${label.id}`, { is_active: newActive }, { headers: { Authorization: `Bearer ${token}` } });
                              setLabels(prev => prev.map(l => l.id === label.id ? { ...l, is_active: newActive } : l));
                              toast.success(newActive ? "Label aktiviert" : "Label deaktiviert");
                            } catch { toast.error("Fehler beim Ändern"); }
                          }}
                          data-testid={`label-toggle-active-${label.id}`}
                          title={label.is_active === false ? "Deaktiviert – klicken zum Aktivieren" : "Aktiv – klicken zum Deaktivieren"}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold transition-all ${
                            label.is_active === false
                              ? "bg-zinc-800 text-zinc-500 hover:bg-zinc-700"
                              : "bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20"
                          }`}
                        >
                          {label.is_active === false
                            ? <><EyeOff size={10} /> Inaktiv</>
                            : <><Eye size={10} /> Aktiv</>}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {(label.manager_ids?.length > 0) ? <CheckCircle2 size={14} className="text-emerald-500 mx-auto" title="Gemanaged" /> : <span className="text-zinc-700">–</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-500">
                        {label.owner_user_id ? <span className="flex items-center gap-1 text-emerald-400"><User size={11} /> Zugewiesen</span> : "–"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => setEditLabel(label)} data-testid={`label-edit-${label.id}`}
                            className="p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors">
                            <Edit2 size={13} />
                          </button>
                          <button onClick={() => setMergeLabel(label)} data-testid={`label-merge-${label.id}`}
                            className="p-1.5 rounded-lg bg-zinc-800 hover:bg-amber-600/30 text-zinc-400 hover:text-amber-400 transition-colors" title="Zusammenführen">
                            <Merge size={13} />
                          </button>
                          <a href={`/label/${label.slug}`} target="_blank" rel="noreferrer"
                            className="p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors">
                            <ExternalLink size={13} />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pages > 1 && (
              <div className="flex items-center justify-center gap-3">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-400 disabled:opacity-40">
                  <ChevronLeft size={15} />
                </button>
                <span className="text-sm text-zinc-500">{page} / {pages}</span>
                <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}
                  className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-400 disabled:opacity-40">
                  <ChevronRight size={15} />
                </button>
              </div>
            )}
          </>
        )}

        {/* ── Duplicates Tab ── */}
        {tab === "duplicates" && (
          <div className="space-y-6">

            {/* ── Massen-Bereinigung ── */}
            <div className="rounded-2xl border overflow-hidden"
              style={{ background: "rgba(239,68,68,0.03)", borderColor: "rgba(239,68,68,0.15)" }}>
              <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: "rgba(239,68,68,0.1)" }}>
                <Zap size={15} className="text-rose-400" />
                <div>
                  <p className="text-white font-bold text-sm">Massen-Bereinigung – exakte Duplikate</p>
                  <p className="text-zinc-500 text-xs mt-0.5">Gleicher Name (case-insensitive) → behält den Eintrag mit echten Releases, löscht den Rest</p>
                </div>
              </div>
              <div className="p-5 space-y-4">
                {/* Dry-Run Ergebnis */}
                {dedupeReport && (
                  <div className="rounded-xl p-4 space-y-3"
                    style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.15)" }}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="space-y-0.5">
                        <p className="text-white font-bold text-sm flex items-center gap-2">
                          <ShieldAlert size={14} className="text-amber-400" /> Analyse-Ergebnis (Dry-Run)
                        </p>
                        <p className="text-zinc-400 text-xs">
                          <span className="text-rose-400 font-bold">{dedupeReport.to_delete} Duplikate</span> würden gelöscht ·{" "}
                          <span className="text-amber-400 font-semibold">{dedupeReport.to_skip} Gruppen</span> übersprungen (verschiedene Releases → manueller Merge nötig)
                        </p>
                      </div>
                      {dedupeReport.to_delete > 0 && (
                        <button
                          onClick={() => {
                            if (!window.confirm(`Wirklich ${dedupeReport.to_delete} Duplikate unwiderruflich löschen?`)) return;
                            runDedupe(false);
                          }}
                          disabled={dedupeRunning}
                          data-testid="dedupe-execute-btn"
                          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white disabled:opacity-40 transition-all"
                          style={{ background: "linear-gradient(110deg,#dc2626,#b91c1c)", border: "1px solid rgba(239,68,68,0.4)" }}
                        >
                          {dedupeRunning ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                          {dedupeReport.to_delete} Duplikate jetzt löschen
                        </button>
                      )}
                    </div>
                    {/* Preview der ersten 10 */}
                    {dedupeReport.preview?.length > 0 && (
                      <div className="rounded-lg overflow-hidden text-xs" style={{ border: "1px solid rgba(255,255,255,0.05)" }}>
                        <div className="bg-zinc-900/60 px-3 py-2 text-zinc-500 font-semibold uppercase tracking-wider flex gap-4">
                          <span className="flex-1">Wird gelöscht</span>
                          <span className="w-14 text-right">Releases</span>
                          <span className="w-40 truncate text-zinc-600">Behalte</span>
                        </div>
                        {dedupeReport.preview.slice(0, 15).map((d, i) => (
                          <div key={i} className="flex items-center gap-4 px-3 py-2 border-t border-white/[0.04] hover:bg-white/[0.02]">
                            <span className="flex-1 text-zinc-300 truncate">{d.name}</span>
                            <span className="w-14 text-right text-zinc-600 font-mono">{d.releases}</span>
                            <span className="w-40 text-zinc-500 truncate">→ {d.keep_name}</span>
                          </div>
                        ))}
                        {dedupeReport.preview.length > 15 && (
                          <div className="px-3 py-2 text-zinc-600 text-xs border-t border-white/[0.04]">
                            ... und {dedupeReport.to_delete - 15} weitere
                          </div>
                        )}
                      </div>
                    )}
                    {dedupeReport.to_delete === 0 && (
                      <div className="flex items-center gap-2 text-emerald-400 text-sm">
                        <CheckCircle2 size={14} /> Keine exakten Duplikate gefunden.
                      </div>
                    )}
                  </div>
                )}

                {/* Execute-Ergebnis */}
                {dedupeExecuted && (
                  <div className="flex items-center gap-3 rounded-xl p-4"
                    style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)" }}>
                    <CheckCircle2 size={16} className="text-emerald-400 flex-shrink-0" />
                    <div>
                      <p className="text-white text-sm font-bold">Bereinigung abgeschlossen</p>
                      <p className="text-zinc-400 text-xs mt-0.5">
                        <span className="text-emerald-400 font-bold">{dedupeExecuted.deleted} Labels</span> gelöscht ·{" "}
                        {dedupeExecuted.skipped > 0 && <span className="text-amber-400">{dedupeExecuted.skipped} übersprungen (manueller Merge nötig)</span>}
                      </p>
                    </div>
                  </div>
                )}

                {/* Analyse-Button */}
                {!dedupeReport && !dedupeExecuted && (
                  <p className="text-zinc-600 text-xs">Starte die Analyse um zu sehen, wie viele Duplikate bereinigt werden können.</p>
                )}
                <div className="flex gap-3">
                  <button
                    onClick={() => { setDedupeReport(null); setDedupeExecuted(null); runDedupe(true); }}
                    disabled={dedupeRunning}
                    data-testid="dedupe-dryrun-btn"
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-40 transition-all border border-white/[0.08] hover:bg-white/[0.04]"
                  >
                    {dedupeRunning ? <Loader2 size={13} className="animate-spin" /> : <ShieldAlert size={13} className="text-amber-400" />}
                    Analyse starten (Dry-Run)
                  </button>
                </div>
              </div>
            </div>

            {/* ── Bestehender Ähnlichkeits-Check ── */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-zinc-400">Labels mit ähnlichen Namen (Ähnlichkeit &gt;82%) zur manuellen Review.</p>
                <button onClick={fetchDuplicates} disabled={loadingDupes}
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-300 transition-colors">
                  <RefreshCw size={13} className={loadingDupes ? "animate-spin" : ""} /> Aktualisieren
                </button>
              </div>
            </div>
            {loadingDupes ? (
              <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>
            ) : duplicates.length === 0 ? (
              <div className="bg-zinc-900 border border-white/5 rounded-xl p-8 text-center">
                <CheckCircle2 size={28} className="text-emerald-500 mx-auto mb-2" />
                <p className="text-zinc-500">Keine potenziellen Duplikate gefunden.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {duplicates.map((group, gi) => (
                  <div key={gi} className="bg-zinc-900 border border-white/5 rounded-xl p-4 space-y-2">
                    <p className="text-xs text-zinc-500 font-semibold uppercase tracking-wide">Gruppe {gi + 1}</p>
                    <DuplicateGroup
                      group={group}
                      token={token}
                      onGroupChanged={(removedId) => {
                        setDuplicates(prev =>
                          prev
                            .map(g => g.filter(l => l.id !== removedId))
                            .filter(g => g.length > 1)
                        );
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {editLabel && <EditModal label={editLabel} token={token} onClose={() => setEditLabel(null)} onSaved={() => { setEditLabel(null); fetchLabels(); }} />}
      {mergeLabel && <MergeModal sourceLabel={mergeLabel} token={token} onClose={() => setMergeLabel(null)} onMerged={() => { setMergeLabel(null); fetchLabels(); }} />}
      {createModal && (
        <CreateLabelModal
          token={token}
          onClose={() => setCreateModal(false)}
          onCreated={() => { setCreateModal(false); fetchLabels(); }}
        />
      )}
    </AdminLayout>
  );
}
